<template>
    <zen-slide-in :isOpen="isOpen" title="Create Environment">
        <template #icon>
            <zen-icon-create />
        </template>
        <template #body>
            <div class="space-y-1 mb-6">
                <p class="text-sm text-taupe-600">
                    Create a new environment for your project. Each environment
                    has its own set of feature flags and configuration.
                </p>
            </div>

            <div class="space-y-5">
                <div>
                    <label
                        for="environment-name"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Name <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <input
                            id="environment-name"
                            :value="data.name"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    name: $event.target.value,
                                })
                            "
                            @keyup="environmentNameToSlug"
                            type="text"
                            placeholder="Production"
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        />
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        The display name of your environment.
                    </p>
                </div>

                <div>
                    <label
                        for="environment-key"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                    >
                        Key <span class="text-red-600">*</span>
                    </label>
                    <div class="mt-2">
                        <input
                            id="environment-key"
                            :value="data.key"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    key: $event.target.value,
                                })
                            "
                            @keypress="disallowSpecialCharacters"
                            @keyup="checkEnvironmentSlug"
                            type="text"
                            placeholder="production"
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 font-mono transition-shadow"
                        />
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        A unique identifier. Use lowercase letters, numbers, and
                        hyphens only.
                    </p>
                </div>

                <div>
                    <label
                        for="description"
                        class="block text-sm font-medium leading-6 text-taupe-900"
                        >Description</label
                    >
                    <div class="mt-2">
                        <textarea
                            id="description"
                            :value="data.description"
                            @input="
                                $emit('update:data', {
                                    ...data,
                                    description: $event.target.value,
                                })
                            "
                            rows="4"
                            placeholder="Describe the purpose of this environment..."
                            class="block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow"
                        ></textarea>
                    </div>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        Optional description for this environment.
                    </p>
                </div>

                <div>
                    <label
                        for="color"
                        class="block text-sm font-medium leading-6 text-taupe-900 mb-2"
                        >Color</label
                    >
                    <v-swatches
                        :model-value="data.color"
                        @update:model-value="
                            (value) =>
                                $emit('update:data', { ...data, color: value })
                        "
                        inline
                    ></v-swatches>
                    <p class="mt-1.5 text-xs text-taupe-500">
                        Choose a color to help identify this environment.
                    </p>
                </div>

                <div>
                    <div
                        class="block text-sm font-medium leading-6 text-taupe-900 mb-3"
                    >
                        Safety Options
                    </div>
                    <div class="space-y-3">
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                id="comments-required"
                                :checked="data.comments_required"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        comments_required:
                                            $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                            />
                            <label
                                for="comments-required"
                                class="ml-3 text-sm text-taupe-700"
                            >
                                Comment required when changing flag value
                            </label>
                        </div>
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                id="confirmation-required"
                                :checked="data.confirmation_required"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        confirmation_required:
                                            $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                            />
                            <label
                                for="confirmation-required"
                                class="ml-3 text-sm text-taupe-700"
                            >
                                Confirmation required when changing flag value
                            </label>
                        </div>
                        <div class="flex items-start">
                            <input
                                type="checkbox"
                                id="can-overlay"
                                :checked="data.can_overlay"
                                @change="
                                    $emit('update:data', {
                                        ...data,
                                        can_overlay: $event.target.checked,
                                    })
                                "
                                class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                            />
                            <label
                                for="can-overlay"
                                class="ml-3 text-sm text-taupe-700"
                            >
                                Allow temporary flag overlays
                            </label>
                        </div>
                    </div>
                    <p class="mt-2 text-xs text-taupe-500">
                        Add additional safety checks for flag changes in this
                        environment.
                    </p>
                </div>
            </div>
        </template>
        <template #buttons>
            <zen-button @click="$emit('close')" variant="secondary">
                Close
            </zen-button>
            <zen-button
                @click="$emit('confirm')"
                variant="primary"
                :processing="processing"
            >
                Create
            </zen-button>
        </template>
    </zen-slide-in>
</template>

<script>
import { VSwatches } from 'vue3-swatches'
import 'vue3-swatches/dist/style.css'

export default {
    name: 'CreateEnvironmentSlideIn',
    components: { VSwatches },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        data: {
            type: Object,
            required: true,
        },
        processing: {
            type: Boolean,
            default: false,
        },
        environmentNameToSlug: {
            type: Function,
            required: true,
        },
        checkEnvironmentSlug: {
            type: Function,
            required: true,
        },
        disallowSpecialCharacters: {
            type: Function,
            required: true,
        },
    },
    emits: ['close', 'confirm', 'update:data'],
}
</script>
